import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BottomSheet from '../mobile/BottomSheet'
import { KILO } from '../../constants/KILO_THEME'
import {
  computeAgeFromDob,
  displayFullName,
  formatDob,
  formatMemberSince,
  loadAccountProfile,
  profileInitials,
  type AccountProfile,
} from '../../lib/accountProfile'

type AccountDetailsSheetProps = {
  open: boolean
  onClose: () => void
  userId: string | null
  fallbackName?: string
}

type DetailRowProps = {
  label: string
  value: string
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-kilo-line py-3 last:border-b-0">
      <span className={KILO.fieldLabel}>{label}</span>
      <span className={`max-w-[60%] text-right text-sm leading-snug ${KILO.text.primary}`}>{value}</span>
    </div>
  )
}

function formatSex(sex: string | null, t: (key: string) => string): string {
  if (!sex) return t('profile.account.notSet')
  const key = sex.toUpperCase()
  if (key === 'M' || key === 'MALE') return t('profile.account.sexMale')
  if (key === 'F' || key === 'FEMALE') return t('profile.account.sexFemale')
  return sex
}

function formatTrainingGoal(goal: string | null, t: (key: string, options?: { defaultValue?: string }) => string): string {
  if (!goal) return t('profile.account.notSet')
  return t(`onboarding.calibration.trainingGoals.${goal}.label`, { defaultValue: goal.replace(/_/g, ' ') })
}

function formatTrainingAge(age: string | null, t: (key: string, options?: { defaultValue?: string }) => string): string {
  if (!age) return t('profile.account.notSet')
  return t(`onboarding.calibration.trainingAges.${age}.label`, { defaultValue: age.replace(/_/g, ' ') })
}

const AccountDetailsSheet = ({ open, onClose, userId, fallbackName }: AccountDetailsSheetProps) => {
  const { t, i18n } = useTranslation()
  const [profile, setProfile] = useState<AccountProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open || !userId) {
      setProfile(null)
      setError('')
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError('')

    void loadAccountProfile(userId).then(({ profile: loaded, error: loadError }) => {
      if (cancelled) return
      setLoading(false)
      if (loadError) {
        setError(loadError)
        return
      }
      setProfile(loaded)
    })

    return () => {
      cancelled = true
    }
  }, [open, userId])

  const locale = i18n.language

  const fullName = useMemo(() => {
    const fromProfile = profile ? displayFullName(profile) : ''
    return fromProfile || fallbackName?.trim() || t('common.athlete')
  }, [profile, fallbackName, t])

  const age = computeAgeFromDob(profile?.dob ?? null)
  const dobFormatted = formatDob(profile?.dob ?? null, locale)
  const memberSince = formatMemberSince(profile?.created_at ?? null, locale)

  return (
    <BottomSheet open={open} onClose={onClose} title={t('profile.account.title')} variant="dark">
      {loading ? (
        <p className={`py-8 text-center text-sm ${KILO.text.muted}`}>{t('profile.account.loading')}</p>
      ) : error ? (
        <p className="py-6 text-center text-sm text-red-400">{error}</p>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-kilo-accent/30 bg-kilo-accent/10">
              <span className="text-xl font-bold text-kilo-accent">
                {profile ? profileInitials(profile) : (fallbackName?.[0]?.toUpperCase() ?? '?')}
              </span>
            </div>
            <p className={`mt-3 text-lg font-bold ${KILO.text.primary}`}>{fullName}</p>
            {profile?.email ? (
              <p className={`mt-1 text-sm ${KILO.text.muted}`}>{profile.email}</p>
            ) : null}
          </div>

          <div className={`rounded-2xl border ${KILO.border.default} bg-kilo-nested px-4`}>
            <DetailRow label={t('profile.account.name')} value={fullName} />
            <DetailRow
              label={t('profile.account.age')}
              value={age != null ? t('profile.account.ageValue', { age }) : t('profile.account.notSet')}
            />
            <DetailRow
              label={t('profile.account.dateOfBirth')}
              value={dobFormatted ?? t('profile.account.notSet')}
            />
            <DetailRow
              label={t('profile.account.sex')}
              value={formatSex(profile?.sex ?? null, t)}
            />
            <DetailRow
              label={t('profile.account.email')}
              value={profile?.email ?? t('profile.account.notSet')}
            />
            <DetailRow
              label={t('profile.account.trainingGoal')}
              value={formatTrainingGoal(profile?.training_goal ?? null, t)}
            />
            <DetailRow
              label={t('profile.account.experience')}
              value={formatTrainingAge(profile?.training_age ?? null, t)}
            />
            <DetailRow
              label={t('profile.account.timezone')}
              value={profile?.timezone ?? t('profile.account.notSet')}
            />
            <DetailRow
              label={t('profile.account.memberSince')}
              value={memberSince ?? t('profile.account.notSet')}
            />
          </div>

          <p className={`text-center text-[11px] leading-relaxed ${KILO.text.soft}`}>
            {t('profile.account.footer')}
          </p>
        </div>
      )}
    </BottomSheet>
  )
}

export default AccountDetailsSheet
